import { useEffect, useState } from "react";
import { Droplets, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/apiClient";

const STATUS_STYLE = {
  safe: "bg-teal-800 text-white",
  advisory: "bg-[#FCE9DC] text-terracotta-700",
  warning: "bg-[#FDF3F0] text-terracotta-700",
  closed: "bg-terracotta-500 text-white",
  unknown: "bg-sand-200 text-muted-foreground",
};

const STATUS_LABEL = {
  safe: "Safe",
  advisory: "Advisory",
  warning: "Warning",
  closed: "Closed",
  unknown: "No current data",
};

const CLASSIFICATION = {
  excellent: "Excellent",
  good: "Good",
  sufficient: "Sufficient",
  poor: "Poor",
  not_classified: "Not classified",
};

export default function WaterQualityPanel({ slug }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/surf-areas/${slug}/water-quality`).then((r) => setData(r.data)).catch(() => setData({ available: false }));
  }, [slug]);
  if (!data) return null;
  return (
    <div className="card-soft p-6" data-testid="water-quality-panel">
      <div className="flex items-center gap-2">
        <Droplets className="w-5 h-5 text-teal-800" />
        <h3 className="font-display text-xl text-teal-900">Water quality</h3>
      </div>
      {!data.available ? (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="wq-unavailable">
          {data.reason || "No matched bathing water for this area."} An admin can attach one from the surf-area editor.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLE[data.status.status] || STATUS_STYLE.unknown}`} data-testid="wq-status">
              {STATUS_LABEL[data.status.status] || "Unknown"}
            </span>
            {data.status.restriction && (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-terracotta-500 text-white flex items-center gap-1" data-testid="wq-restriction">
                <AlertTriangle className="w-3 h-3" />
                {data.status.restriction.replace(/_/g, " ")}
              </span>
            )}
          </div>
          <div className="text-sm text-teal-900">
            <b>Classification (annual):</b> {CLASSIFICATION[data.status.classification] || data.status.classification || "—"}
          </div>
          <div className="text-xs text-muted-foreground">
            Monitored at <b className="text-teal-900">{data.status.site_name || "the matched bathing water"}</b>
            {data.status.distance_km != null && <> · {data.status.distance_km.toFixed(2)} km away</>}
          </div>
          <div className="text-[11px] text-muted-foreground pt-1 border-t border-border">
            {data.status.envelope?.attribution || data.status.attribution || "Official monitoring data"}
            {data.status.envelope?.note && <> · {data.status.envelope.note}</>}
          </div>
          {data.status.classification === "poor" && (
            <div className="text-xs text-terracotta-700 pt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> The annual classification is a historical measure, not a current safety indicator.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
